import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { progressService } from "../../../../../services/progressService";

export default function MeetingPersonalityCard({
  mode = "data",       // "data" | "api"
  userId,
  data = null,
  compact = false,
  selectable = true,
  selected = false,
  onSelect = () => {},
}) {
  const [cardData, setCardData] = useState(null);
  const [, setLoading] = useState(mode === "api");

  /* -----------------------------------------------------
   * MODE: DIRECT DATA (no API call)
   * ---------------------------------------------------*/
  useEffect(() => {
    if (mode !== "data") return;
    setCardData(data);
  }, [mode, data]);

  /* -----------------------------------------------------
   * MODE: API MODE → fetch card from backend
   * ---------------------------------------------------*/
  useEffect(() => {
    if (mode !== "api" || !userId) return;

    let cancelled = false;

    async function load() {
      try {
        setLoading(true);

        const res = await progressService.getUserDataForSection(
          userId,
          "course_career_progression",
          "presence",
          "rootcause_widget"
        );

        console.log("Fetched card:", res);

        const apiCard = res?.selectedRootCause; // <-- FIXED
        console.log("API Card Data:", apiCard);

        if (!cancelled && apiCard) {
          setCardData(apiCard);
        }
      } catch (err) {
        console.error("Card API error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => (cancelled = true);
  }, [mode, userId]);

  /* -----------------------------------------------------
   * LOADING STATE — return a transparent shell
   * (prevents ugly ‘Loading…’ in animated sequences)
   * ---------------------------------------------------*/
  if (!cardData) {
    return (
      <div
        className="relative bg-white"
        style={{
          width: 470,
          height: 582,
          borderRadius: "23.3px",
          opacity: 0, // invisible placeholder
        }}
      />
    );
  }

  /* -----------------------------------------------------
   * UI RENDER
   * ---------------------------------------------------*/
  const scale = compact ? 0.73 : 1;
  const border = compact
    ? "0.85px solid rgba(84,176,175,0.30)"
    : selected
    ? "1.17px solid #54B0AF"
    : "1.17px solid #54B0AF88";

  return (
    <div
      className="relative bg-white"
      onClick={() => selectable && !compact && onSelect(cardData.id)}  // <-- FIXED
      style={{
        width: 470,
        height: 570,
        border,
        borderRadius: "23.3px",
        transform: `scale(${scale})`,
        transformOrigin: "top left",
        opacity: compact ? 0.75 : 1,
        cursor: selectable && !compact ? "pointer" : "default",
      }}
    >
      {/* SELECT TICK */}
      {!compact && selectable && (
        <div
          className="absolute top-6 right-6 w-7 h-7 rounded-full border-2 border-[#54B0AF] flex items-center justify-center"
          style={{ background: selected ? "#54B0AF" : "white" }}
        >
          {selected && <Check size={16} className="text-white" />}
        </div>
      )}

      {/* ICON */}
      <div
        className="absolute overflow-hidden"
        style={{ top: 6, left: 8, width: 220, height: 180 }}
      >
        <img
          src={cardData.icon}
          alt={cardData.title}
          className="w-full h-full object-cover"
          style={{ objectPosition: "center 32%" }}
        />
      </div>

      {/* TEXT */}
      <div className="absolute" style={{ top: 195, left: 40, right: 40 }}>
        <h2 className="text-2xl font-semibold">{cardData.title}</h2>
        <p className="mt-2 text-sm text-gray-700">{cardData.subtitle}</p>

        <div className="mt-8 space-y-6">
          <Block label="TRAITS" text={cardData.traits} />
          <Block label="LIKES" text={cardData.likes} />
          <Block label="DISLIKES" text={cardData.dislikes} />
        </div>
      </div>
    </div>
  );
}

const Block = ({ label, text }) => (
  <div>
    <p className="text-xs font-semibold text-[#169E9C]">{label}</p>
    <p className="text-sm mt-1">{text}</p>
    <div className="border-b mt-2" />
  </div>
);
